import React, { useState } from "react";
import { Loader2, Search, Download, Trash2, CheckCircle2 } from "lucide-react";
import { Button } from "@cms/ui/components/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@cms/ui/components/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@cms/ui/components/select";
import { Input } from "@cms/ui/components/input";
import { Badge } from "@cms/ui/components/badge";
import { ConfirmDeleteDialog } from "@cms/ui/components/confirm-dialog";
import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell
} from "@cms/ui/components/table";
import type { ApiClient } from "@cms/portal-lib/api";
import { formatTimestamp } from "@cms/portal-lib/format";
import type { AdminClient } from "../lib/outlet-context";

interface SarSubmission {
  form_id: string;
  created_at: string;
  data: Record<string, unknown>;
}

interface SarResult {
  email: string;
  total: number;
  submissions: SarSubmission[];
}

/** Flatten a submission's fields to a one-line "key: value" preview. */
function summarizeData(data: Record<string, unknown>): string {
  return Object.entries(data)
    .map(([key, value]) => `${key}: ${typeof value === "string" ? value : JSON.stringify(value)}`)
    .join(" · ");
}

function downloadJson(filename: string, payload: unknown): void {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function GdprPanel({ clients, api }: { clients: AdminClient[]; api: ApiClient | null }) {
  const [selectedId, setSelectedId] = useState("");
  const [email, setEmail] = useState("");
  const [result, setResult] = useState<SarResult | null>(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [erasing, setErasing] = useState(false);
  const [erased, setErased] = useState<number | null>(null);

  const canSearch = Boolean(selectedId && email.trim() && api && !searching);

  const search = async () => {
    if (!canSearch || !api) return;
    setSearching(true);
    setError(null);
    setErased(null);
    try {
      const data = await api.request<SarResult>(
        `/admin/gdpr/${selectedId}/submissions?email=${encodeURIComponent(email.trim())}`
      );
      setResult(data ?? null);
    } catch (err) {
      setResult(null);
      setError((err as Error).message);
    } finally {
      setSearching(false);
    }
  };

  const erase = async () => {
    if (!api || !result) return;
    setErasing(true);
    setError(null);
    try {
      const data = await api.request<{ deleted: number }>(
        `/admin/gdpr/${selectedId}/submissions?email=${encodeURIComponent(result.email)}`,
        { method: "DELETE" }
      );
      setErased(data?.deleted ?? 0);
      setResult({ ...result, total: 0, submissions: [] });
      setConfirmOpen(false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setErasing(false);
    }
  };

  const exportSar = () => {
    if (!result) return;
    downloadJson(`sar-${selectedId}-${result.email}.json`, {
      tenant: selectedId,
      subject_email: result.email,
      exported_at: new Date().toISOString(),
      total: result.total,
      submissions: result.submissions
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>GDPR subject requests</CardTitle>
        <CardDescription>
          Find every form submission a person has made on a client's site by their email
          address — export the set as JSON for a subject-access request, or erase it
          (including uploaded files) for a right-to-erasure request. Erasures are recorded
          in the audit log.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-56 flex-1 space-y-2">
            <label className="text-sm font-medium">Select client</label>
            <Select value={selectedId} onValueChange={(id) => { setSelectedId(id); setResult(null); setErased(null); }}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a client..." />
              </SelectTrigger>
              <SelectContent>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name} ({c.id})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-64 flex-1 space-y-2">
            <label className="text-sm font-medium">Subject email</label>
            <Input
              type="email"
              value={email}
              placeholder="person@example.com"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              onKeyDown={(e: React.KeyboardEvent) => { if (e.key === "Enter") void search(); }}
            />
          </div>
          <Button onClick={search} disabled={!canSearch}>
            {searching ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Searching...</>
            ) : (
              <><Search className="h-4 w-4" /> Find submissions</>
            )}
          </Button>
        </div>

        {!api && (
          <p className="text-sm text-muted-foreground">
            GDPR tooling requires a configured API. Set PUBLIC_API_BASE_URL to enable.
          </p>
        )}
        {error && <p className="text-sm text-destructive">Request failed: {error}</p>}
        {erased !== null && (
          <Badge variant="success" className="gap-1">
            <CheckCircle2 className="h-3 w-3" />
            Erased {erased} submission{erased === 1 ? "" : "s"} for {email.trim()}
          </Badge>
        )}

        {result && erased === null && (
          <SarResults result={result} onExport={exportSar} onErase={() => setConfirmOpen(true)} />
        )}

        <ConfirmDeleteDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          title="Erase this person's submissions?"
          description={
            `Permanently deletes ${result?.total ?? 0} submission(s) matching ` +
            `${result?.email ?? ""} from ${selectedId}, including any uploaded files. ` +
            "This cannot be undone — export the SAR JSON first if a copy is needed."
          }
          confirmLabel="Erase submissions"
          busy={erasing}
          onConfirm={() => void erase()}
        />
      </CardContent>
    </Card>
  );
}

function SarResults({ result, onExport, onErase }: {
  result: SarResult;
  onExport: () => void;
  onErase: () => void;
}) {
  if (result.total === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No submissions found for {result.email}.
      </p>
    );
  }
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium">
          {result.total} submission{result.total === 1 ? "" : "s"} for {result.email}
        </p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onExport}>
            <Download className="h-4 w-4" /> Export JSON
          </Button>
          <Button variant="destructive" onClick={onErase}>
            <Trash2 className="h-4 w-4" /> Erase all
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Form</TableHead>
              <TableHead>Submitted</TableHead>
              <TableHead>Data</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {result.submissions.map((submission, index) => {
              const summary = summarizeData(submission.data);
              return (
                <TableRow key={`${submission.form_id}-${submission.created_at}-${index}`}>
                  <TableCell className="whitespace-nowrap font-medium">{submission.form_id}</TableCell>
                  <TableCell className="whitespace-nowrap">{formatTimestamp(submission.created_at)}</TableCell>
                  <TableCell className="max-w-96 truncate text-muted-foreground" title={summary}>
                    {summary}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
